'use client';

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react';
import {
  LoadingIndicator,
  ModalNavigationController,
  NavigationManager,
  RouteHandler,
  type StackEntry,
} from '@sdk-mobile';
import {AnimatedStack} from '@/app/(app)/games/[gid]/_src/SkillzSDK/AnimatedStack';
import {useRouter} from 'next/navigation';
import {remapShellPath} from '@/utils/shellRoute';

const SdkRouterContext = createContext<{stack: StackEntry[]}>({stack: []});

const SDKProvider = ({children}: PropsWithChildren) => {
  const router = useRouter();
  const stack = useSyncExternalStore(
    (callback) => {
      NavigationManager.subscribe(callback);
      return () => {
        NavigationManager.unsubscribe(callback);
      };
    },
    () => NavigationManager.stack
  );

  useEffect(() => {
    const handlePopState = () => {
      // Browser's forward/back buttons act only on Next.js router, ignoring Legacy Stack.
      // So if user uses them, at least make sure legacy stack does not obstructs the view after navigation.
      if (stack.length) {
        NavigationManager.popNavigator();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stack.length]);

  const animationType = window.innerWidth <= 768 ? 'slide' : 'fade';

  const onNavigationChange = useCallback(
    (action: 'push' | 'replace' | 'back' | 'backToRoot' | 'refreshWeb', url?: string) => {
      if (stack.length) {
        if (action === 'push' || action === 'replace' || action === 'back') {
          const legacyScreen = stack[stack.length - 1]?.scene || 'Unknown';

          if (process.env.NODE_ENV === 'production') {
            console.error(
              `Navigation Error: ${action} from ${legacyScreen} on ${window.location.pathname} to ${url}`
            );
          } else {
            console.warn(
              `Navigation Refactor: MIXED NAVIGATORS DETECTED; Legacy Screen: "${legacyScreen}"; Target URL: "${url}"; Action: "${action}"; Previous React Route: "${window.location.pathname}"; Check SkillzSDK.warnOnLegacyStackVisible() for more info`
            );
          }

          // when SkillzRouter.push() / .replace() / back() is called
          // reset web stack navigator to show page content (children)
          NavigationManager.updateStack([]);
        }
      }

      if (action === 'backToRoot') {
        // when SkillzRouter.backToRoot() is called
        // reset web stack navigator and redirect to home
        NavigationManager.popToRoot();
      }

      if (action === 'refreshWeb') {
        router.refresh();
      }
    },
    [stack, router.refresh]
  );

  const handleLegacyBack = useCallback(
    (navigatorBack: () => void) => {
      if (stack.length) {
        NavigationManager.popNavigator();
      } else {
        navigatorBack();
      }
    },
    [stack.length]
  );

  return (
    <SdkRouterContext.Provider value={{stack}}>
      <AnimatedStack animationType={animationType} stack={stack}>
        {children}
      </AnimatedStack>
      <ModalNavigationController />
      <LoadingIndicator />
      {/* remapUrl: on the multicade shell surface (getGameId()===19369) every SDK nav
          builds `/games/19369/{dest}`, which has no page — remap it to the clean
          top-level / partner URL. No-op for real games (id ≠ 19369) and already-clean
          paths, so it is safe to pass unconditionally. */}
      <RouteHandler
        onNavigationChange={onNavigationChange}
        handleLegacyBack={handleLegacyBack}
        remapUrl={remapShellPath}
      />
    </SdkRouterContext.Provider>
  );
};

export default SDKProvider;
